import {
	Box,
	Inline,
	Table,
	AsyncSelect,
	AsyncSelectProps,
	useBreakpoints,
	Dropdown,
	Icons,
	Stack,
	Text,
	Button,
	Spinner,
	ContentToggle,
	Time,
	Divider,
	CheckboxInput,
	joinAttributes,
	Select,
} from "@sembark-travel/ui/base"
import {
	ButtonLink,
	Link,
	NavLink,
	useLocationQuery,
} from "@sembark-travel/ui/router"
import {
	areAdvancedFiltersAppliedDefault,
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui/list"
import { ICursorListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import React, {
	Fragment,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"
import { Omit } from "utility-types"
import { generatePath } from "../router-utils"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { AddTransportServiceDialog } from "./NewItem"
import {
	checkFilterAppliedFn,
	ITransportService,
	TTransportServiceLocation,
	transportServicesXHR as XHR,
} from "./store"
import { TemporaryStorage } from "../TemporaryStorage"
import config from "../config"
import { IUser, PERMISSIONS, useCheckPermissions } from "../Auth"
import {
	DateRangePickerField,
	Form,
	isTruthy,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"
import {
	dateToUTCString,
	endOf,
	formatDate,
	startOf,
} from "@sembark-travel/datetime-utils"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import * as Validator from "yup"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import useSWR from "swr"
import { SelectUsers } from "../Users"

type TFilters = TSearchParams & {
	expire_after?: string | Date
	expire_before?: string | Date
	last_updated_after?: string | Date
	last_updated_before?: string | Date
	disabled_only?: 1 | 0
	trip_destinations?: Array<TTripDestination>
	last_updated_by?: Array<IUser>
	upload_method?: Array<{ name: string; id: string }>
}

function filtersToAPIParams(params: TFilters) {
	const { upload_method, ...restData } = params
	return {
		...restData,
		expire_after: params.expire_after
			? dateToUTCString(startOf(params.expire_after, "day"))
			: null,
		expire_before: params.expire_before
			? dateToUTCString(endOf(params.expire_before, "day"))
			: null,
		last_updated_after: params.last_updated_after
			? dateToUTCString(startOf(params.last_updated_after, "day"))
			: null,
		last_updated_before: params.last_updated_before
			? dateToUTCString(endOf(params.last_updated_before, "day"))
			: null,
		expire_after_local: params.expire_after
			? formatDate(startOf(params.expire_after, "day"), "YYYY-MM-DD")
			: null,
		expire_before_local: params.expire_before
			? formatDate(endOf(params.expire_before, "day"), "YYYY-MM-DD")
			: null,
		disabled_only: isTruthy(params.disabled_only) ? 1 : null,
		trip_destinations: params.trip_destinations?.map((t) => t.id),
		last_updated_by: params.last_updated_by?.map((t) => t.id),
		...params.upload_method?.reduce<Record<string, string>>((acc, item) => {
			if (item.id) {
				acc[item.id] = "1"
			}
			return acc
		}, {}),
	}
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectTripDestination}
				name="trip_destinations"
				label="Destinations"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={Select}
				label="Entry Method"
				name="upload_method"
				multiple
				options={[
					{
						name: "Added by sheet only",
						id: "only_uploaded_by_sheet",
					},
					{
						name: "Added manual only",
						id: "only_manual_uploaded",
					},
					{
						name: "Mix-by manual and by sheet",
						id: "mixed_only",
					},
				]}
			/>
			<DateRangePickerField
				label="Rate Validity"
				fromName="expire_after"
				toName="expire_before"
			/>
			<SelectField
				select={SelectUsers}
				name="last_updated_by"
				label="Last Updated By"
				fetchOnMount
				multiple
			/>
			<DateRangePickerField
				label="Last Updated Between"
				fromName="last_updated_after"
				toName="last_updated_before"
			/>
			<SwitchInputField name="disabled_only" label="Disabled Only" />
		</Stack>
	)
}
const serviceStyle = {
	padding: "8px",
}

export function List() {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const [key, setKey] = useState(0)
	const { xs, sm } = useBreakpoints()
	const { hasPermission } = useCheckPermissions()

	return (
		<Search
			areAdvancedFiltersApplied={(params) => {
				const { disabled_only, ...otherParams } = params
				return (
					isTruthy(disabled_only) ||
					areAdvancedFiltersAppliedDefault(otherParams)
				)
			}}
			initialParams={params}
			Filters={Filters}
			resetParams={{ q: "" }}
			onSearch={(params) => {
				setParams({ ...params, cursor: null })
			}}
			title="Transport Services"
			actions={({ changeAdvancedFiltersVisibility }) => (
				<Inline gap="4">
					<ButtonLink
						to={generatePath("/transport-services/new")}
						level="secondary"
					>
						New Service
					</ButtonLink>
					<Dropdown alignRight={!xs}>
						<Dropdown.ToggleButton>
							<Icons.DotsVertical />
						</Dropdown.ToggleButton>
						<Dropdown.Menu>
							{hasPermission(PERMISSIONS.UPLOAD_BULK_TRANSPORT_SERVICES) ? (
								<NavLink to={generatePath("/transport-service-prices")}>
									<Icons.BankNotes /> View Prices
								</NavLink>
							) : null}
							<NavLink to={generatePath("/cab-types")}>
								<Icons.Taxi /> Cab Types
							</NavLink>
							{hasPermission(PERMISSIONS.UPLOAD_BULK_TRANSPORT_SERVICES) ? (
								<>
									<Dropdown.MenuItemDivider />
									<TemporaryStorage
										initUrl="/transport-services/download"
										initParams={{
											...filtersToAPIParams(params),
											disabled_only: isTruthy(params.disabled_only) ? 1 : null,
											timezone_offset: config.timezoneOffset,
										}}
									>
										{({ message, generate, url, reset }) =>
											url ? (
												<Dropdown.MenuItem
													as="a"
													href={url}
													target="_blank"
													download
													onClick={() => reset()}
												>
													{message}
												</Dropdown.MenuItem>
											) : (
												<Dropdown.MenuItem
													onClick={(e: React.MouseEvent) => {
														e.preventDefault()
														generate()
													}}
												>
													{message || (
														<>
															<Icons.DocumentDownload /> Download as CSV/Excel
														</>
													)}
												</Dropdown.MenuItem>
											)
										}
									</TemporaryStorage>
									<DisableServicesInDialog
										onSuccess={() => {
											setKey((key) => ++key)
										}}
										params={params}
										key={key}
										FilterOpen={() => changeAdvancedFiltersVisibility(true)}
									>
										{({ onDisable }) => (
											<Dropdown.MenuItem onClick={onDisable}>
												<Icons.Ban /> Bulk Disable Transport{" "}
												{xs ? <br /> : null} Services
											</Dropdown.MenuItem>
										)}
									</DisableServicesInDialog>
								</>
							) : null}
						</Dropdown.Menu>
					</Dropdown>
				</Inline>
			)}
		>
			<Fragment key={key}>
				<CursorListView<TTransportServiceLocation, TFilters>
					pageKey="/transport-services-locations-list"
					params={params}
					fetch={(xhr, params) =>
						XHR(xhr).getTransportServiceLocations({
							...filtersToAPIParams(params),
							disabled_only: isTruthy(params.disabled_only) ? 1 : null,
							limit: 30,
							include: "thumbs,rate_uploaded_by,updated_by,created_by",
						})
					}
					onCursorChange={(cursor) => setParams({ ...params, cursor })}
				>
					{({ items: transportLocations }) =>
						!xs && !sm ? (
							<Box>
								<Table
									responsive
									bordered
									hover
									headers={[
										"",
										<Box style={{ maxWidth: "150px" }}>From</Box>,
										<Box style={{ maxWidth: "150px" }}>To</Box>,
										<Inline alignItems={"end"} justifyContent="between">
											<Box style={{ width: "50%" }}>Services</Box>
											<Box
												wordBreak="words"
												whiteSpace="normal"
												style={{ width: "10%" }}
												textAlign={"center"}
											>
												Entry Method
											</Box>
											<Box
												wordBreak="words"
												whiteSpace="normal"
												style={{ width: "20%" }}
												textAlign={"center"}
											>
												Rate Validity
											</Box>
											<Box
												wordBreak="words"
												whiteSpace="normal"
												style={{ width: "20%" }}
												textAlign={"center"}
											>
												Last Updated
											</Box>
										</Inline>,
									]}
									rows={transportLocations.map(
										({
											id,
											short_code,
											from,
											to,
											services,
											thumb_image_url,
											is_disabled,
										}) => [
											thumb_image_url ? (
												<Box
													as="img"
													src={thumb_image_url}
													alt="Display"
													borderWidth="1"
													rounded="md"
													style={{ maxWidth: "100px" }}
												/>
											) : (
												<Icons.Bus color={"accent"} size="8" />
											),
											<Link
												anchored
												color="accent"
												fontWeight="semibold"
												to={generatePath(
													"/transport-services/:serviceLocationId",
													{
														serviceLocationId: id.toString(),
													}
												)}
												style={{ maxWidth: "150px" }}
											>
												<Text>{from?.name}</Text>

												{short_code ? (
													<Box fontSize="sm">{short_code}</Box>
												) : null}
											</Link>,
											<Text style={{ maxWidth: "150px" }}>{to?.name}</Text>,
											<Box
												style={{
													borderTop: "1px solid #ccc",
													borderCollapse: "collapse",
												}}
											>
												<ContentToggle
													gap={"0"}
													renderItem={(s: ITransportService) => (
														<Inline
															key={s.id}
															flexWrap="wrap"
															alignItems="stretch"
															borderBottomWidth="1"
															borderBottomColor="emphasis"
														>
															<Box
																style={{
																	width: "50%",
																	...serviceStyle,
																}}
																borderLeftWidth={"1"}
																borderBottomColor="emphasis"
															>
																<Inline alignItems={"center"} gap={"1"}>
																	<Text>{s.service}</Text>
																	{s.deleted_at ? (
																		<Box>
																			<Icons.Ban color={"danger"} />
																		</Box>
																	) : null}
																</Inline>
															</Box>
															<Box
																textAlign="center"
																style={{
																	width: "10%",
																	...serviceStyle,
																}}
																borderBottomColor="emphasis"
																borderLeftWidth={"1"}
															>
																{s.entry_type === "manually" ? (
																	<Icons.HandPencil />
																) : (
																	<Icons.Sheet />
																)}
															</Box>
															<Box
																textAlign="center"
																wordBreak="words"
																whiteSpace="normal"
																style={{
																	...serviceStyle,
																	width: "20%",
																}}
																borderLeftWidth={"1"}
																borderBottomColor="emphasis"
															>
																<Inline>
																	<Time
																		timestamp={s.rate_expiry_date ?? undefined}
																		localTimestamp={
																			s.rate_expiry_date_local ?? undefined
																		}
																		format="DD MMM, YYYY"
																		wordBreak="words"
																		whiteSpace="normal"
																		textAlign={"left"}
																	/>
																</Inline>

																{s.rate_uploaded_by?.name ? (
																	<Inline alignItems={"center"}>
																		<Box>
																			<Icons.User />
																		</Box>
																		<Text
																			wordBreak="words"
																			whiteSpace="normal"
																			color={"muted"}
																			textAlign={"left"}
																		>
																			{s.rate_uploaded_by?.name}
																		</Text>
																	</Inline>
																) : (
																	<Box>-</Box>
																)}
															</Box>

															<Box
																wordBreak="words"
																whiteSpace="normal"
																style={{
																	...serviceStyle,
																	width: "20%",
																}}
																borderLeftWidth={"1"}
																borderRightWidth={"1"}
																borderBottomColor="emphasis"
																textAlign="center"
															>
																<Inline>
																	<Time
																		timestamp={s.updated_at || s.created_at}
																		format="DD MMM, YYYY"
																		wordBreak="words"
																		whiteSpace="normal"
																		textAlign={"left"}
																	/>
																</Inline>
																<Inline alignItems={"center"}>
																	<Box>
																		<Icons.User />
																	</Box>
																	<Text
																		wordBreak="words"
																		whiteSpace="normal"
																		color={"muted"}
																		textAlign={"left"}
																	>
																		{s.updated_by?.name || s.created_by?.name}
																	</Text>
																</Inline>
															</Box>
														</Inline>
													)}
													data={services}
												/>
											</Box>,
										]
									)}
								/>
							</Box>
						) : (
							<Box as="ol">
								{transportLocations.map(
									({ id, name, services, thumb_image_url }) => (
										<Box
											as="li"
											key={id}
											padding="4"
											borderWidth="1"
											rounded="lg"
											marginBottom="4"
											bgColor="subtle"
										>
											<Box marginBottom="4">
												<Link
													fontSize="md"
													to={generatePath(
														"/transport-services/:serviceLocationId",
														{
															serviceLocationId: id.toString(),
														}
													)}
													color="accent"
													fontWeight="semibold"
												>
													<Inline gap="4">
														{thumb_image_url ? (
															<Box
																as="img"
																src={thumb_image_url}
																alt="Display"
																rounded="md"
																borderWidth="1"
																style={{ width: "100px", minWidth: "100px" }}
															/>
														) : null}
														<Box>{name}</Box>
													</Inline>
												</Link>
											</Box>
											<ContentToggle
												renderItem={(s: ITransportService) => (
													<>
														<Text
															textAlign={"left"}
															fontWeight="semibold"
															fontSize={"md"}
														>
															{s.service}
														</Text>
														<Inline flexWrap={"wrap"}>
															{joinAttributes(
																<Box>
																	{s.entry_type === "manually"
																		? "Manually"
																		: "By Sheet"}
																</Box>,

																s.rate_expiry_date_local ? (
																	<Box>
																		uploaded on :{" "}
																		<Time
																			timestamp={
																				s?.rate_expiry_date ?? undefined
																			}
																			localTimestamp={
																				s.rate_expiry_date_local ?? undefined
																			}
																			format="DD MMM, YYYY"
																			whiteSpace="nowrap"
																			wordBreak="words"
																		/>
																	</Box>
																) : null,
																s.rate_uploaded_by ? (
																	<Inline gap={"1"}>
																		rate expiry by :{" "}
																		<Text
																			wordBreak="words"
																			whiteSpace="normal"
																			color={"muted"}
																		>
																			{s.rate_uploaded_by?.name ?? "-"}
																		</Text>
																	</Inline>
																) : null,

																<Text wordBreak="words" whiteSpace="normal">
																	last updated by:{" "}
																	{s?.updated_by?.name || s?.created_by?.name}
																</Text>,
																<Box>
																	last updated on :{" "}
																	<Time
																		timestamp={s.updated_at || s.created_at}
																		localTimestamp={
																			s.rate_expiry_date_local ??
																			s.created_at ??
																			undefined
																		}
																		format="DD MMM, YYYY"
																		wordBreak="words"
																		whiteSpace="normal"
																	/>
																</Box>
															)}
														</Inline>
														<Divider sm />
													</>
												)}
												data={services}
											/>
										</Box>
									)
								)}
							</Box>
						)
					}
				</CursorListView>
			</Fragment>
		</Search>
	)
}

export function SelectTransportServices({
	tripDestinations,
	...props
}: Omit<AsyncSelectProps, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
}) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const promiseOfCreation = useRef<
		((transportService: ITransportService) => void) | undefined
	>(undefined)
	return (
		<Fragment>
			<AsyncSelect
				createOptionLabel={(q) => `Add "${q}"`}
				multiple
				optionRenderer={({
					option,
					created,
				}: {
					option: ITransportService
					created?: boolean
				}) =>
					created ? (
						<Box>Add "{option.name}"</Box>
					) : (
						<Box>
							<Box>
								{option.from_to}
								{option.locations?.short_code ? (
									<Box as="code" marginLeft="1">
										- {option.locations.short_code}
									</Box>
								) : null}
							</Box>
							<Box fontSize="sm">{option.service}</Box>
						</Box>
					)
				}
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				{...props}
				fetch={(q) =>
					XHR(xhr)
						.getTransportServices({
							q,
							trip_destinations: tripDestinations?.length
								? tripDestinations.map((t) => t.id)
								: null,
						})
						.then((resp) => resp.data)
				}
			/>
			<AddTransportServiceDialog
				open={!!newName}
				onClose={() => {
					setNewName("")
				}}
				initialValues={{
					trip_destinations: tripDestinations,
				}}
				onSuccess={(transportService) => {
					promiseOfCreation.current?.(transportService)
				}}
				type="addLocationService"
				serviceLocationId={undefined}
				canAddMoreServices={false}
				url={`/transport-services`}
			/>
		</Fragment>
	)
}

export function SelectTransportServiceLocations({
	tripDestinations,
	...props
}: Omit<AsyncSelectProps, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
}) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const promiseOfCreation = useRef<
		((transportServiceLocation: TTransportServiceLocation) => void) | undefined
	>(undefined)
	return (
		<Fragment>
			<AsyncSelect
				createOptionLabel={(q) => `Add "${q}"`}
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				{...props}
				perFetchLimit={30}
				fetch={(q, { page }) =>
					XHR(xhr)
						.getTransportServiceLocations({
							limit: 30,
							q,
							trip_destinations: tripDestinations?.length
								? tripDestinations.map((t) => t.id)
								: null,
							pagination: "simple",
							include: "created_by",
							page,
						})
						.then((resp) => resp.data)
				}
				optionRenderer={({
					option,
					created,
				}: {
					option: TTransportServiceLocation
					created?: boolean
				}) =>
					created ? (
						<Box>Add "{option.name}"</Box>
					) : (
						<Box>
							{option.name}
							{option.short_code ? (
								<Box as="code" marginLeft="1">
									- {option.short_code}
								</Box>
							) : null}
						</Box>
					)
				}
			/>
			<AddTransportServiceDialog
				open={!!newName}
				initialValues={{
					trip_destinations: tripDestinations,
				}}
				onClose={() => {
					setNewName("")
				}}
				onSuccess={(transportService) => {
					promiseOfCreation.current?.(transportService.locations)
				}}
				type="addLocationService"
				serviceLocationId={undefined}
				canAddMoreServices={false}
				url={`/transport-services`}
			/>
		</Fragment>
	)
}

interface RefHandle {
	getUnselectedIds: () => number[]
	isItemZero: boolean
}

export function DisableServicesInDialog({
	children,
	params,
	onSuccess,
	FilterOpen,
}: {
	children: (props: { onDisable: () => void }) => React.ReactNode
	params: TFilters
	onSuccess: () => void
	FilterOpen: () => void
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [count, setCount] = useState({
		count: 0,
		totalCount: 0,
	})
	const xhr = useXHR()
	const childRef = useRef<RefHandle>(null)
	const inProgressRef = useRef(false)
	const [inProgressState, setInProgressState] = useState(false)
	const [isSelectAll, setIsSelectAll] = useState(false)

	useEffect(() => {
		if (!inProgressRef.current && inProgressState) {
			showSnackbar("Disabling transport services...")
		}
		if (inProgressRef.current && !inProgressState) {
			showSnackbar("Transport services disabled successfully!")
			closeDialog()
		}
		inProgressRef.current = inProgressState
	}, [inProgressState, closeDialog])
	useEffect(() => {
		async function getCount() {
			try {
				const item = await XHR(xhr).getTransportServiceLocationsWithPaginate({
					...filtersToAPIParams(params),
					disabled_only: isTruthy(params.disabled_only) ? 1 : null,
					limit: 1,
					page: 1,
					pagination: "paginate",
				})
				setCount({
					count: 0,
					totalCount: item.meta.total || 0,
				})
			} catch (e) {
				console.log(e)
			}
		}
		if (isOpen && checkFilterAppliedFn(params)) {
			getCount()
		}
		if (!isOpen) {
			setCount({
				count: 0,
				totalCount: 0,
			})
			setIsSelectAll(false)
		}
	}, [isOpen, params, xhr])
	const decreaseIncrease = (type: "decrease" | "increase") => {
		setCount((val) => ({
			...val,
			count: type === "decrease" ? --val.count : ++val.count,
		}))
	}
	const openFilter = () => {
		closeDialog()
		FilterOpen()
	}
	if (isTruthy(params.disabled_only)) return

	return (
		<Batch
			onFinish={() => {
				closeDialog()
				onSuccess()
			}}
		>
			{({ init, xhr, inProgress }) => {
				if (inProgress !== inProgressState) {
					setInProgressState(inProgress)
				}
				return (
					<>
						{children({ onDisable: () => openDialog() })}
						<Dialog
							open={isOpen}
							onClose={closeDialog}
							title="Bulk Disable Transport Services"
							lg
						>
							<Form<{ confirmed_services_name: number }>
								validate={validateFormValues(
									Validator.object().shape({
										confirmed_services_name: Validator.number()
											.equals(
												[count.count],
												`Please type ${count.count} to confirm the disabling`
											)
											.not([0], "Please type a valid number")
											.required(
												"Please type the count to confirm the disabling"
											),
									})
								)}
								onSubmit={withServerErrors(async () => {
									const resp = await xhr.delete(
										`/transport-service-locations-bulk-destroy`,
										{
											data: {
												...filtersToAPIParams(params),
												...(isSelectAll
													? {
															exclusions: childRef.current
																?.getUnselectedIds()
																?.map((id) => id),
														}
													: {
															inclusions: childRef.current
																?.getUnselectedIds()
																?.map((id) => id),
														}),
											},
										}
									)
									init(resp.data.data)
								})}
								subscription={{ submitting: true }}
							>
								{({ submitting, handleSubmit }) => (
									<form noValidate onSubmit={handleSubmit}>
										<Dialog.Body padding="8">
											<Inline justifyContent={"between"} gap={"2"}>
												<Text fontSize="lg" fontWeight="semibold">
													Please review details before disable
												</Text>
												{!isSelectAll &&
												checkFilterAppliedFn(params) &&
												!childRef.current?.isItemZero ? (
													<Inline
														cursor="pointer"
														as="label"
														alignItems="center"
														gap="2"
													>
														<CheckboxInput
															checked={isSelectAll}
															onChange={(e) => {
																setIsSelectAll(true)
																setCount((val) => {
																	return { ...val, count: val.totalCount }
																})
															}}
															name="selected_all"
															id="selected_all"
														/>
														<Box htmlFor="selected_all" as="label">
															Select All
														</Box>
													</Inline>
												) : null}
											</Inline>
											<InfiniteScroll
												decreaseIncrease={decreaseIncrease}
												ref={childRef}
												params={params}
												openFilter={openFilter}
												isSelectAll={isSelectAll}
											/>
											{checkFilterAppliedFn(params) &&
											!childRef.current?.isItemZero ? (
												<>
													<Text color="primary">
														<Icons.Info /> Disabling these services is not
														permanent. You can restore them at any time.
													</Text>

													{count.count ? (
														<Box marginTop="8">
															<TextInputField
																label={`Please Type ${count.count} to confirm`}
																name="confirmed_services_name"
																type="number"
																aria-label="Type in the number of the Services to confirm that you want to disable these Services"
																required
															/>
														</Box>
													) : null}
												</>
											) : null}
										</Dialog.Body>
										{checkFilterAppliedFn(params) &&
										!childRef.current?.isItemZero ? (
											<Dialog.Footer>
												<SubmissionError />
												<Button
													disabled={
														submitting ||
														(inProgressRef.current && !inProgressState)
													}
													type="submit"
													status="danger"
													level="primary"
												>
													{inProgressRef.current && !inProgressState ? (
														"Disabling..."
													) : (
														<>
															<Icons.Ok /> I understand, disable these Services
														</>
													)}
												</Button>
												<Button
													disabled={submitting}
													onClick={() => closeDialog()}
													level="tertiary"
												>
													Cancel
												</Button>
											</Dialog.Footer>
										) : null}
									</form>
								)}
							</Form>
						</Dialog>
					</>
				)
			}}
		</Batch>
	)
}

type CursorMeta = ICursorListResponse<unknown>["meta"]
type TInfiniteScrollBase = {
	params: TFilters
	decreaseIncrease: (type: "decrease" | "increase") => void
	openFilter: () => void
	isSelectAll: boolean
}

function InfiniteScrollBase(
	{ params, decreaseIncrease, openFilter, isSelectAll }: TInfiniteScrollBase,

	ref: React.ForwardedRef<RefHandle>
) {
	const [items, setItems] = useState<TTransportServiceLocation[]>([])
	const [selectedItmes, setSelectedItmes] = useState<number[]>([])
	type Status = {
		isLoading: boolean
		error: string
	}
	const [status, setStatus] = useState<Status>({
		isLoading: false,
		error: "",
	})

	const groupRef = useRef<HTMLDivElement>(null)
	const isFetchingRef = useRef(false)
	const pageInfo = useRef<CursorMeta>()
	const SCROLL_THRESHOLD = 100
	const xhr = useXHR()

	const getServiceLocations = useCallback(async () => {
		if (isFetchingRef.current) return
		setStatus((val) => ({ ...val, isLoading: true }))
		isFetchingRef.current = true
		try {
			const item = await XHR(xhr).getTransportServiceLocations({
				...filtersToAPIParams(params),
				disabled_only: isTruthy(params.disabled_only) ? 1 : null,
				limit: 15,
				cursor: pageInfo.current?.next_cursor,
			})
			pageInfo.current = item.meta

			setItems((prev) =>
				pageInfo.current?.prev_cursor ? [...prev, ...item.data] : [...item.data]
			)
		} catch (e) {
			const error = e as Error
			setStatus((val) => ({
				...val,
				error: error.message || "Something went wrong",
			}))
		} finally {
			isFetchingRef.current = false
			setStatus((val) => ({ error: "", isLoading: false }))
		}
	}, [xhr, params])
	useEffect(() => {
		if (checkFilterAppliedFn(params)) {
			getServiceLocations()
		}
		let throttleTimeout: ReturnType<typeof setTimeout> | null = null
		const throttledHandleScroll = () => {
			if (throttleTimeout) return
			throttleTimeout = setTimeout(() => {
				const container = groupRef.current?.querySelector("ol")
				if (!container) return

				const isBottom =
					container.scrollHeight - container.scrollTop <=
					container.clientHeight + SCROLL_THRESHOLD

				if (
					isBottom &&
					!isFetchingRef.current &&
					pageInfo.current?.next_cursor
				) {
					getServiceLocations()
				}

				throttleTimeout = null
			}, 300)
		}
		const container = groupRef.current?.querySelector("ol")
		if (container) {
			container.addEventListener("scroll", throttledHandleScroll)
			return () => {
				container.removeEventListener("scroll", throttledHandleScroll)
				if (throttleTimeout) clearTimeout(throttleTimeout)
			}
		}
	}, [getServiceLocations, params])

	React.useImperativeHandle(ref, () => ({
		getUnselectedIds: function () {
			return selectedItmes
		},
		isItemZero: !items.length && !status.isLoading,
	}))
	useEffect(() => {
		setSelectedItmes([])
	}, [isSelectAll])
	return (
		<Stack
			padding="4"
			borderWidth="1"
			borderColor="warning"
			rounded="lg"
			marginBottom="4"
			ref={groupRef}
		>
			{status.error ? (
				<Box
					display="flex"
					alignItems="center"
					gap={"3"}
					padding={"3"}
					borderColor={"danger"}
					borderRadius="md"
				>
					<Box as="span" flex="1" fontWeight="semibold">
						{status.error}
					</Box>
					<Box
						as="button"
						paddingX={"3"}
						paddingY={"1"}
						color="white"
						borderRadius="md"
						fontWeight="semibold"
						onClick={getServiceLocations}
					>
						Refresh
					</Box>
				</Box>
			) : null}
			<Box
				style={{
					height: "400px",
				}}
				overflowY="auto"
				as="ol"
				display={!items.length && !status.isLoading ? "flex" : "block"}
				flexDirection={"col"}
				justifyContent={"center"}
				alignItems={"center"}
			>
				{!items.length && !status.isLoading ? (
					<>
						<Box as="p">
							Please choose right filter or search option to disable any service
						</Box>
						<Button onClick={openFilter} type="button">
							Choose Right Filter
						</Button>
					</>
				) : (
					items.map(({ id, name, services }) => (
						<Box
							as="li"
							key={id}
							padding="4"
							borderWidth="1"
							rounded="lg"
							marginBottom="4"
							bgColor="subtle"
						>
							<Box marginBottom="2">
								<Inline cursor="pointer">
									<CheckboxInput
										id={name + id}
										checked={
											isSelectAll
												? !selectedItmes.includes(id)
												: selectedItmes.includes(id)
										}
										onChange={(e) => {
											const checked =
												(e.currentTarget.checked && isSelectAll) ||
												(!e.currentTarget.checked && !isSelectAll)
											if (checked) {
												setSelectedItmes((ids) =>
													ids.filter((singleId) => singleId !== id)
												)
												decreaseIncrease(isSelectAll ? "increase" : "decrease")
											} else {
												setSelectedItmes((ids) => [...ids, id])
												decreaseIncrease(!isSelectAll ? "increase" : "decrease")
											}
										}}
									/>
									<Box htmlFor={name + id} as="label">
										{name}
									</Box>
								</Inline>
							</Box>
							<Inline gap="1" flexWrap="wrap">
								{services?.map((s) => (
									<Box
										key={s.id}
										display="inlineBlock"
										paddingX="2"
										borderWidth="1"
										borderColor="default"
										rounded="lg"
									>
										{s.service}
									</Box>
								))}
							</Inline>
						</Box>
					))
				)}
				{status.isLoading ? (
					<Inline justifyContent={"center"} gap={"2"} alignItems={"center"}>
						<Spinner /> Loading more services...
					</Inline>
				) : null}
			</Box>
		</Stack>
	)
}
const InfiniteScroll = React.forwardRef<RefHandle, TInfiniteScrollBase>(
	InfiniteScrollBase
)

export type TBatch = {
	id: string
	name: string
	totalJobs: number
	pendingJobs: number
	processedJobs: number
	progress: number
	failedJobs: number
	createdAt: string
	cancelledAt: string | null
	finishedAt: string | null
}

export function Batch({
	children,
	onFinish,
}: {
	onFinish: () => void
	children: (props: {
		init: (batch: TBatch) => void
		xhr: XHRInstance
		inProgress: boolean
	}) => React.ReactNode
}) {
	const [batch, setBatch] = useState<null | TBatch>(null)
	const xhr = useXHR()
	const { data: currentBatch } = useSWR(
		batch ? `/batch/${batch.id}` : null,
		() =>
			xhr
				.get<{ data: TBatch }>(`batches/${batch?.id}`)
				.then((resp) => resp.data.data)
				.then((batch) => {
					if (batch.finishedAt) {
						onFinish()
					}
					return batch
				}),
		{
			refreshInterval: 1000,
		}
	)
	return (
		<>
			{children({
				xhr,
				init: (batch) => setBatch(batch),
				inProgress: Boolean(currentBatch && !currentBatch.finishedAt),
			})}
		</>
	)
}
